import { useAuthStore, type UserRole } from '../stores/auth.store';
import { canByRole, type Action, type Subject } from './abilities.rules';

/**
 * Frontend abilities — React tarafı.
 *
 * Rules tablosu pure-data olarak `abilities.rules.ts`'te tutulur ki backend
 * parity test (jest) onu import edebilsin. Burada sadece React entegrasyonu
 * (hook + component) sağlanır.
 *
 * Pattern: `useAbility().can('action', 'subject')` ile sorgulanır.
 *
 * NOT: Bu sadece **UI gating** içindir. Gerçek authorization backend'de
 * AbilitiesGuard tarafından yapılır.
 */

export type { Action, Subject } from './abilities.rules';

export interface Ability {
  can(action: Action, subject: Subject): boolean;
  cannot(action: Action, subject: Subject): boolean;
  role: UserRole | null;
}

function buildAbility(role: UserRole | null): Ability {
  return {
    role,
    can(action, subject) {
      return canByRole(role, action, subject);
    },
    cannot(action, subject) {
      return !canByRole(role, action, subject);
    },
  };
}

/**
 * Hook — auth store'dan role çeker, ability döner.
 */
export function useAbility(): Ability {
  const role = useAuthStore((s) => s.user?.role ?? null);
  return buildAbility(role);
}

/**
 * React component — koşullu UI gating için.
 *
 * <Can do="create" on="Card">
 *   <Button>Yeni Kart</Button>
 * </Can>
 */
export function Can({
  do: action,
  on,
  fallback = null,
  children,
}: {
  do: Action;
  on: Subject;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}) {
  const ability = useAbility();
  return <>{ability.can(action, on) ? children : fallback}</>;
}
